"use client";

import React from "react";
import Link from "next/link";
// import { useRouter } from "next/navigation";
// import { useQueryClient } from "@tanstack/react-query";
import { Typography } from "@/components/typography";
import TruckIcon from "@/assets/icons/truck";
// import { success, error } from "@/components/alert";
// import useSettingsStore from "@/global-store/settings";
// import useAddressStore from "@/global-store/address";
// import useUserStore from "@/global-store/user";
// import useCartStore from "@/global-store/cart";
// import { cartService } from "@/services/cart";
// import { InsertCartPayload, Cart } from "@/types/cart";
// import { DefaultResponse } from "@/types/global";
// import NetworkError from "@/utils/network-error";
// import { extractProductsFromCart } from "@/utils/extract-products-from-cart";
import { StatusPill } from "./status-pill";
import { OrderItem } from "./order-item";
import type { ApiOrder, OrderStatus } from "../types";

interface OrderCardProps {
  order: ApiOrder;
}

const formatDate = (dateStr: string): string => {
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
};

const formatCurrency = (amount: number): string => `$${amount.toFixed(2)}`;

const computeOrderTotal = (order: ApiOrder): number => {
  const itemsTotal = order.orderDetails.reduce(
    (sum, detail) => sum + detail.totalPrice,
    0,
  );
  const taxTotal = order.orderDetails.reduce(
    (sum, item) => sum + (item.tax || 0),
    0,
  );
  return (
    itemsTotal + order.shippingFee - order.totalDiscount + taxTotal
  );
};

const normalizeStatus = (status: string): OrderStatus => {
  if (!status) return "new";
  const normalized = status.toLowerCase().replace(/[\s-]+/g, "_");
  const statusMap: Record<string, OrderStatus> = {
    new: "new",
    accepted: "accepted",
    ready: "ready",
    on_the_way: "on_the_way",
    canceled: "canceled",
    delivered: "delivered",
    shipped: "on_the_way",
    processing: "new",
    pending: "new",
    confirmed: "accepted",
  };
  return statusMap[normalized] ?? "new";
};

export const OrderCard = ({ order }: OrderCardProps) => {
  // const router = useRouter();
  // const queryClient = useQueryClient();
  // const currency = useSettingsStore((state) => state.selectedCurrency);
  // const country = useAddressStore((state) => state.country);
  // const city = useAddressStore((state) => state.city);
  // const user = useUserStore((state) => state.user);
  // const updateCartLocal = useCartStore((state) => state.updateList);
  // const addToCartLocal = useCartStore((state) => state.addProduct);
  // const memberCartId = useCartStore((state) => state.memberCartId);
  // const userCartUuid = useCartStore((state) => state.userCartUuid);
  // const isMemberOfAnotherCart = !!memberCartId && !!userCartUuid;

  // const { mutate: insertCart } = useMutation({
  //   mutationFn: (data: InsertCartPayload) => cartService.insert(data),
  //   onError: (err: NetworkError) => error(err.message),
  //   onSuccess: (res: DefaultResponse<Cart>) => {
  //     if (user?.id) {
  //       const products = extractProductsFromCart(res.data, user.id);
  //       updateCartLocal(products);
  //     }
  //     queryClient.setQueriesData<DefaultResponse<Cart> | undefined>(
  //       { queryKey: ["cart"], exact: false },
  //       () => res,
  //     );
  //     success("Items added to cart");
  //     router.push("/cart");
  //   },
  // });

  // const { mutate: insertGuestCart } = useMutation({
  //   mutationFn: (data: InsertCartPayload) => cartService.insertGuest(data),
  //   onError: (err: NetworkError) => error(err.message),
  //   onSuccess: (res: DefaultResponse<Cart>) => {
  //     if (userCartUuid) {
  //       const products = extractProductsFromCart(res.data, userCartUuid);
  //       updateCartLocal(products);
  //     }
  //     queryClient.setQueriesData<DefaultResponse<Cart> | undefined>(
  //       { queryKey: ["cart"], exact: false },
  //       () => res,
  //     );
  //     success("Items added to cart");
  //     router.push("/cart");
  //   },
  // });

  // const handleBuyAgain = () => {
  //   const products = order.orderDetails
  //     .map((d) => {
  //       const stockId =
  //         d.orderProductsVariants?.[0]?.companyProductVariantId?.id;
  //       if (!stockId) return null;
  //       return { stock_id: stockId, quantity: d.quantity };
  //     })
  //     .filter((p): p is { stock_id: number; quantity: number } => p !== null);
  //   if (products.length === 0) {
  //     error("No items with variant information can be reordered.");
  //     return;
  //   }
  //   if (!currency || !country) {
  //     error("Please set your location and currency.");
  //     return;
  //   }
  //   const body: InsertCartPayload = {
  //     currency_id: currency.id,
  //     rate: currency.rate,
  //     region_id: country.region_id,
  //     country_id: country.id,
  //     products,
  //     city_id: city?.id,
  //   };
  //   if (isMemberOfAnotherCart && memberCartId != null && userCartUuid != null) {
  //     body.cart_id = memberCartId;
  //     body.user_cart_uuid = userCartUuid;
  //     insertGuestCart(body);
  //   } else if (user) {
  //     insertCart(body);
  //   } else {
  //     products.forEach((p) => addToCartLocal(p.stock_id, p.quantity));
  //     success("Items added to cart");
  //     router.push("/cart");
  //   }
  // };

  const status = normalizeStatus(order.status);
  const total = computeOrderTotal(order);
  const shipTo = `${order.userId.firstName} ${order.userId.lastName}`;
  const itemCount = order.orderDetails.reduce((sum, d) => sum + d.quantity, 0);
  const deliveryAddress = order.addressId
    ? [order.addressId.address, order.addressId.city, order.addressId.country]
        .filter(Boolean)
        .join(", ")
    : "";

  return (
    <article className="group overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-sm transition-all duration-300 hover:shadow-xl hover:border-gray-300">
      {/* Header */}
      <div className="relative border-b border-gray-200 bg-pageBg px-6 py-4">
        <div className="grid md:grid-cols-4 grid-cols-2 gap-4">
          <div className="flex flex-col items-start">
            <Typography
              tag="span"
              className="block text-sm uppercase tracking-wider text-gray-500"
            >
              Order placed
            </Typography>
            <Typography
              tag="span"
              className="block text-sm font-semibold text-gray-800"
            >
              {formatDate(order.createdAt)}
            </Typography>
          </div>
          <div className="flex flex-col items-start">
            <Typography
              tag="span"
              className="block text-sm uppercase tracking-wider text-gray-500"
            >
              Total
            </Typography>
            <Typography
              tag="span"
              className="block text-sm font-bold text-primary"
            >
              {formatCurrency(total)}
            </Typography>
          </div>
          <div className="flex flex-col items-start">
            <Typography
              tag="span"
              className="block text-sm uppercase tracking-wider text-gray-500"
            >
              Ship to
            </Typography>
            <Typography
              tag="span"
              className="block text-sm font-semibold text-gray-800"
            >
              {shipTo}
            </Typography>
          </div>
          <div className="flex flex-col items-start md:items-end">
            <Typography
              tag="span"
              className="block text-sm uppercase tracking-wider text-gray-500"
            >
              Order #
            </Typography>
            <Typography tag="span" className="text-sm font-bold text-primary">
              {order.id}
            </Typography>
            {order.referenceId && (
              <Typography
                tag="span"
                className="block text-xs text-gray-600 mt-1"
              >
                Ref: {order.referenceId}
              </Typography>
            )}
          </div>
        </div>
      </div>

      {/* Status & shipping info */}
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-4">
          <StatusPill status={status} />
          {deliveryAddress && (
            <div className="hidden md:flex items-center gap-1.5 text-sm text-gray-500">
              <TruckIcon size={16} className="text-gray-400 shrink-0" />
              <span className="line-clamp-1 text-gray-600">
                {deliveryAddress}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span>
            {itemCount} item{itemCount !== 1 ? "s" : ""}
          </span>
          <span className="w-1 h-1 rounded-full bg-gray-300" />
          {order.shippingFee > 0 ? (
            <span>Shipping: {formatCurrency(order.shippingFee)}</span>
          ) : (
            <span className="text-green-600 font-medium">Free Shipping</span>
          )}
          {order.totalDiscount > 0 && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="text-green-600 font-medium">
                -{formatCurrency(order.totalDiscount)} saved
              </span>
            </>
          )}
        </div>
      </div>

      {/* Product list */}
      <div className="px-6 py-4">
        <div className="flex gap-3 overflow-x-auto pb-1 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent md:flex-wrap md:overflow-visible">
          {order.orderDetails.map((detail) => (
            <OrderItem
              key={detail.id}
              item={detail}
              productHref={
                detail.productId?.slug
                  ? `/products/${detail.productId.slug}`
                  : undefined
              }
            />
          ))}
        </div>
      </div>

      {/* Footer actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-[#FAFBFC] px-6 py-3">
        <div className="flex flex-wrap gap-2">
          {/* <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-all hover:bg-gray-50 hover:border-gray-300 hover:text-black"
          >
            <DownloadIcon size={14} />
            Invoice
          </button> */}
          {/* <button
            type="button"
            onClick={handleBuyAgain}
            disabled={isAddingToCart || isAddingToGuestCart}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-all hover:bg-gray-50 hover:border-gray-300 hover:text-black disabled:opacity-50"
          >
            <RefreshIcon size={14} />
            Buy Again
          </button> */}
          {/* <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 transition-all hover:bg-gray-50 hover:border-gray-300 hover:text-black"
          >
            <InfoIcon size={14} />
            Help
          </button> */}
        </div>
        <Link
          href={`/orders/${order.id}`}
          aria-label={`View details for order #${order.id}`}
          className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20 active:scale-[0.98]"
        >
          View Details
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </article>
  );
};
